import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRightIcon, BellIcon, MapPinIcon, MoonIcon, SunIcon } from '@heroicons/react/16/solid';
import { memberNotificationService, memberViolationService } from '../../clients/ApiClients';
import { NotificationResponse, ViolationReturn } from '../../clients/rust-api-client';
import { formatHomeAddress, useMember } from '../../Hooks/MemberContext';
import { useTheme } from '../Custom/ThemeProvider';
import { StatusIcon } from '../../Violations/linear/StatusIcon';
import { shortRef, statusLabel } from '../../Violations/linear/violationMeta';
import { CommandPalette, usePaletteShortcut } from './CommandPalette';
import { Command, CommandGroup } from './commandRanking';

/**
 * The homeowner's ⌘K palette (#603 follow-on 4), mounted once for the
 * signed-in app — not in the sidebar, which the layout renders twice on a
 * phone. Same palette as front-porch; what it holds is a member's world:
 * their pages, their homes, the violations on them and their mail.
 *
 * Everything here is a read or a navigation. Nothing a homeowner does to
 * money happens in this app at all, and it will not start in a palette.
 */
interface PaletteContextType {
  openPalette: () => void;
}

const PaletteContext = createContext<PaletteContextType>({ openPalette: () => undefined });

export const useCommandPalette = () => useContext(PaletteContext);

const FRESH_FOR_MS = 2 * 60 * 1000;
const PUBLIC_PATHS = ['/login', '/callback'];
/** Recent mail is what anyone jumps to; older is a scroll away on the page. */
const NOTIFICATION_LIMIT = 50;

interface Searchable {
  violations: ViolationReturn[];
  notifications: NotificationResponse[];
}

const EMPTY: Searchable = { violations: [], notifications: [] };

export const CommandPaletteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { member, homes, selectedHome, selectHome } = useMember();
  const memberId = member?.member_id ?? null;
  const signedIn = !!memberId && !PUBLIC_PATHS.includes(pathname);

  const [open, setOpen] = useState(false);
  const [data, setData] = useState<Searchable>(EMPTY);
  const [loading, setLoading] = useState(false);
  const loadedAt = useRef(0);
  const inFlight = useRef(false);
  const currentMember = useRef(memberId);

  // A different member, or nobody: forget what the last one could see.
  useEffect(() => {
    currentMember.current = memberId;
    setData(EMPTY);
    loadedAt.current = 0;
    setOpen(false);
  }, [memberId]);

  const load = useCallback(async () => {
    if (inFlight.current || Date.now() - loadedAt.current < FRESH_FOR_MS) return;
    inFlight.current = true;
    setLoading(true);
    const forMember = memberId;
    try {
      const [vio, notes] = await Promise.allSettled([
        memberViolationService.listMyViolations(),
        memberNotificationService.listMyNotifications({ perPage: NOTIFICATION_LIMIT }),
      ]);
      if (currentMember.current !== forMember) return;
      setData({
        violations: vio.status === 'fulfilled' ? vio.value.data.violations : [],
        notifications: notes.status === 'fulfilled' ? notes.value.data.notifications : [],
      });
      loadedAt.current = Date.now();
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [memberId]);

  const setPaletteOpen = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) load();
    },
    [load]
  );
  usePaletteShortcut(signedIn, open, setPaletteOpen);

  const groups = useMemo((): CommandGroup[] => {
    const go = (to: string) => () => navigate(to);
    const addressOf = new Map(homes.map((h) => [h.home_id, formatHomeAddress(h)]));
    const page = (to: string, label: string, keywords: string[] = []): Command => ({
      id: `page:${to}`,
      label,
      keywords,
      hint: 'Page',
      icon: <ArrowRightIcon className="h-3.5 w-3.5" />,
      run: go(to),
    });

    const actions: Command[] = [
      {
        id: 'action:theme',
        label: isDarkMode ? 'Switch to light theme' : 'Switch to dark theme',
        keywords: ['theme', 'dark', 'light', 'mode', 'appearance'],
        hint: 'Action',
        icon: isDarkMode ? <SunIcon className="h-3.5 w-3.5" /> : <MoonIcon className="h-3.5 w-3.5" />,
        run: toggleDarkMode,
      },
    ];

    // Only when there is a choice to make, as in the sidebar.
    const homeItems: Command[] =
      homes.length > 1
        ? homes
            .filter((h) => h.home_id !== selectedHome?.home_id)
            .map((h) => ({
              id: `home:${h.home_id}`,
              label: `Switch to ${formatHomeAddress(h)}`,
              detail: h.hoa_name,
              keywords: ['home', 'account', 'property'],
              hint: 'Home',
              icon: <MapPinIcon className="h-3.5 w-3.5" />,
              run: () => {
                selectHome(h.home_id);
                navigate('/account');
              },
            }))
        : [];

    return [
      { key: 'actions', label: 'Actions', items: actions },
      { key: 'homes', label: 'Your homes', items: homeItems },
      {
        key: 'pages',
        label: 'Go to',
        items: [
          page('/home', 'Home', ['dashboard']),
          page('/account', 'Account', ['balance', 'dues', 'billing']),
          page('/account/activity', 'Account activity', ['payments', 'charges', 'ledger', 'history']),
          page('/account/statements', 'Statements', ['pdf', 'bill']),
          page('/notifications', 'Notifications', ['inbox', 'messages', 'mail']),
          page('/violations', 'Violations'),
          page('/amenities', 'Amenities', ['book', 'reserve', 'reservation', 'clubhouse', 'pool', 'court']),
          page('/reservations', 'My reservations', ['bookings', 'booked', 'reserve', 'cancel']),
          page('/settings', 'Settings', ['profile', 'preferences']),
        ],
      },
      {
        key: 'violations',
        label: 'Violations',
        idleLimit: 0,
        items: data.violations.map((v) => ({
          id: `violation:${v.id}`,
          label: v.description,
          detail: [shortRef(v.id), statusLabel(v.status), homes.length > 1 ? addressOf.get(v.home_id) : null]
            .filter(Boolean)
            .join(' · '),
          keywords: [v.violation_type, v.severity],
          hint: 'Violation',
          icon: <StatusIcon status={v.status} />,
          run: go(`/violation/${v.id}`),
        })),
      },
      {
        key: 'notifications',
        label: 'Notifications',
        idleLimit: 0,
        items: data.notifications.map((n) => ({
          id: `notification:${n.id}`,
          label: n.title,
          detail: [n.is_read ? null : 'Unread', n.hoa_name].filter(Boolean).join(' · '),
          keywords: [n.notification_type, n.is_read ? '' : 'unread'].filter(Boolean),
          hint: 'Notification',
          icon: <BellIcon className="h-3.5 w-3.5" />,
          run: go(`/notification/${n.id}`),
        })),
      },
    ];
  }, [data, navigate, homes, selectedHome, selectHome, isDarkMode, toggleDarkMode]);

  const context = useMemo(() => ({ openPalette: () => setPaletteOpen(true) }), [setPaletteOpen]);

  return (
    <PaletteContext.Provider value={context}>
      {children}
      {signedIn && (
        <CommandPalette
          open={open}
          onClose={() => setOpen(false)}
          groups={groups}
          loading={loading}
          placeholder="Search your violations and notifications — or jump to a page…"
        />
      )}
    </PaletteContext.Provider>
  );
};
